import { tok, tokPx, UnavailableContentDialog } from '@sunwebgroup/ui-components'
import { Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'

type Breakpoint =
  | 'primitives_breakpoint_m_min'
  | 'primitives_breakpoint_m_max'
  | 'primitives_breakpoint_l_min'

const Table = styled.table`
  ${Typography.modVariant('body-m-regular-tight')}
  width: 100%;
  border-collapse: collapse;
`

const sharedCellStyles = css<{ $hideOn?: Breakpoint, $size?: 'lg' }>`
  padding: ${tokPx('primitives_space_16')} 0;
  width: ${({ $size }) => ($size === 'lg' ? '240px' : '140px')};

  &:first-child {
    flex-grow: 1;
  }

  &:nth-last-child(2) {
    text-align: right;
    width: 100px;
  }

  &:last-child {
    width: ${tokPx('primitives_space_20')};
    margin-right: -${tokPx('primitives_space_12')};
  }

  @media (max-width: ${tokPx('primitives_breakpoint_m_min')}) {
    padding: ${tokPx('primitives_space_12')} 0;
  }

  ${({ $hideOn }) =>
    $hideOn
    && css`
      @media (max-width: ${tokPx($hideOn)}) {
        display: none;
    }
    `}
`

const TableHead = styled.th<{ $hideOn?: Breakpoint, $size?: 'lg' }>`
  color: ${tok('semantics_content_secondary_color')};
  text-align: left;
  ${sharedCellStyles}
`

const TableCell = styled.td<{ $hideOn?: Breakpoint, $size?: 'lg', $isMuted?: boolean }>`
  ${sharedCellStyles}
  align-content: center;

  &:first-child {
    ${Typography.modVariant('body-m-bold-tight')}
  }

  ${({ $isMuted }) =>
    $isMuted
    && css`
      color: ${tok('semantics_content_secondary_color')};
    `}
`

const TableRow = styled.tr<{ $isSelected?: boolean }>`
  display: flex;
  padding: 0 ${tokPx('primitives_space_16')};
  gap: ${tokPx('primitives_space_16')};
  border-bottom: 2px solid ${tok('semantics_dividers_color')};
  background-color: ${props => props.$isSelected
    ? tok('primitives_color_selected_50')
    : 'transparent'};

  @media (max-width: ${tokPx('primitives_breakpoint_m_min')}) {
    padding: 0 ${tokPx('primitives_space_12')};
    gap: ${tokPx('primitives_space_12')};
  }
`

const Subtitle = styled(Typography)`
  ${Typography.modVariant('body-s-regular-tight')}
  color: ${tok('semantics_content_secondary_color')};
`

const TableCellWithSubtitle = styled(TableCell)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-wrap: break-word;
`

const TableCellChevron = styled(TableCell)`
  ${Icon} {
    display: flex;
  }
`

function EmptyTableMessage() {
  const intl = useIntl()

  const StyledUnavailableContentDialog = styled(UnavailableContentDialog)`
    margin-top: ${tokPx('primitives_space_64')};
  `

  return (
    <StyledUnavailableContentDialog
      icon={<Icon glyph="FaceSad" size={32} />}
      title={intl.formatMessage({ id: 'groups_responsible__no_data_title' })}
      message={intl.formatMessage({ id: 'groups_responsible__no_data_description' })}
    />
  )
}

function ChevronCell({ $hideOn }: { $hideOn?: Breakpoint }) {
  return (
    <TableCellChevron $hideOn={$hideOn}>
      <Icon glyph="ChevronRight" size={20} />
    </TableCellChevron>
  )
}

export { ChevronCell, EmptyTableMessage, Subtitle, Table, TableCell, TableCellWithSubtitle, TableHead, TableRow }
